/**
 * DomainShape SVG component
 * Displays territory shapes for different French domains (France, DOM-TOM)
 * Similar to Hexagone but supports multiple territories
 */
import { Box } from '@mui/material';
import { memo } from 'react';
import { DOMAIN_PATHS, DOMAIN_VIEWBOX } from './domain-paths.js';

/**
 * DomainShape component for displaying different territory shapes
 * @param {Object} props - Component props
 * @param {string} props.domain - Territory identifier (France hexagonale, Réunion, Guadeloupe, etc.)
 * @param {Object} props.bgProps - Properties for the background rect
 * @param {Object} props.pathProps - Properties for the SVG path
 * @param {Object} props.sx - Material-UI sx styling prop
 * @returns {React.ReactElement} SVG territory shape component
 */
const DomainShape = ({
  domain = 'France hexagonale',
  bgProps = {},
  pathProps = {},
  sx = {},
  ...props
}) => {
  const pathData = DOMAIN_PATHS[domain];

  if (!(domain in DOMAIN_PATHS)) {
    // eslint-disable-next-line no-console
    console.warn(`DomainShape: Unknown domain "${domain}". Available: ${Object.keys(DOMAIN_PATHS).join(', ')}`);
    return null;
  }

  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={DOMAIN_VIEWBOX}
      // Decorative by default: the shape backs the outlook badge, whose horizon
      // and warming level are already spelled out in adjacent text.
      aria-hidden="true"
      sx={{ display: 'inline-block', ...sx }}
      {...props}
    >
      <rect
        width="85.04"
        height="85.04"
        fill="#02568d"
        rx="42.52"
        ry="42.52"
        {...bgProps}
      />
      {pathData && (
        <path
          fill="#fff"
          stroke="#fff"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          strokeWidth="1.4"
          d={pathData}
          {...pathProps}
        />
      )}
    </Box>
  );
};

DomainShape.displayName = 'DomainShape';

// Use React.memo for performance optimization
export default memo(DomainShape);
